<template>
  <div class="mt-2 px-6 inactive-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <LibraryList>
        <LibraryListItemWrapper
          v-for="{
            ulid,
            title,
            location,
            job_level: jobLevel,
            job_code: jobCode,
          } in jobProfileDrafts"
          :key="ulid"
        >
          <router-link
            :to="{ query: { preview: ulid, type: 'jobProfile' } }"
          >
            <DraftJobProfilesItem
              v-bind="{
                title, location, jobLevel, jobCode,
              }"
            />
          </router-link>
        </LibraryListItemWrapper>
      </LibraryList>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { computed, onMounted, onBeforeUnmount } from 'vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import DraftJobProfilesItem from '@/components/jobProfiles/DraftJobProfilesItem.vue';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppPagination } from '@/stores/app/pagination';
import StickyPagination from '@/components/shared/StickyPagination.vue';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { jobProfileDrafts } = storeToRefs(useJobProfileDrafts());
const { loadJobProfileDrafts } = useJobProfileDrafts();
const { sendFilterRequest, setFilterConfig, setSearchTerm } = useFilters();
const { showFilterBarSettings } = useAppBase();
const { totalPages, page } = storeToRefs(useAppPagination());

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const loadPage = async (params) => {
  const mode = params.mode === 'draft-job-profiles' ? 'draft' : params.mode;

  const updatedParams = {
    ...params,
    mode,
  };

  await loadJobProfileDrafts(updatedParams);
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    showMyJobs: true,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  if (route.query?.q) setSearchTerm(route.query.q);

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'draft',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });

  emit('track-page-load', { page: 'Job Profile Drafts: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });
});
</script>
